import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { CarouselProvider, Slider, Slide, DotGroup, Dot, ButtonNext, ButtonBack } from 'pure-react-carousel';
import ExternalLink from '../../Common/ExternalLink/ExternalLink';
import VenetianBlindsText from '../../Common/VenetianBlindsText/VenetianBlindsText';
import { linkTypes } from '../../../models/Models';
import { formatParagraphs } from '../../../models/Utils';
import { useWindowSize } from '../../../models/Hooks';
import './InfographicCarousel.scss';
import { ADAARIARoles } from '../../../models/ADA';
import { element } from 'screenfull';

const InfographicCarousel = ({ cardData, isInline }) => {
  const activeKey = useSelector(state => state.data.activeSlideKey);
  const menuOpened = useSelector(state => state.data.menuOpened);
  const windowSize = useWindowSize();
  const [currentSlide, setCurrentSlide] = useState(false);
  const [isActive, setActiveState] = useState(false);
  const [elmtsPerSlide, setElmtsPerSlide] = useState(1);
  const [naturalSlideWidth, setNaturalSlideWidth] = useState('100vw');
  const carouselRef = useRef(null);
  const controlRef = useRef(null);
  const animationDelay = 0.25;

  useEffect(() => {
    if (cardData && activeKey === cardData.Key) {
      setActiveState(true);
    } else {
      setActiveState(false);
    }
  }, [activeKey, cardData]);

  useEffect(() => {
    const n = Math.floor(windowSize.width / 360) || 1;
    setElmtsPerSlide(n > 3 ? 3 : n);
    setNaturalSlideWidth(windowSize.width > 1272 ? '1272px' : '100vw');
  }, [windowSize]);

  useLayoutEffect(() => {
    if (!carouselRef.current || !controlRef.current) {
      return;
    }
    let checkForScroll = true;
    let maxDeltaX = 0;
    const handleScroll = e => {
      if(!isInline){
        e.preventDefault();
      }
      const scrollThreshold = 2;
      if (checkForScroll && Math.abs(e.deltaY) < Math.abs(e.deltaX)) {
        if (Math.abs(e.deltaX) > Math.abs(maxDeltaX) && Math.abs(maxDeltaX) <= scrollThreshold) {
          maxDeltaX = e.deltaX;
        } else {
          checkForScroll = false;
          if (Math.abs(maxDeltaX) > scrollThreshold) {
            const carouselContext = controlRef.current.context;
            if (maxDeltaX > 0) {
              if (carouselContext.state.currentSlide < carouselContext.state.totalSlides - 1) {
                controlRef.current.context.setStoreState({
                  currentSlide: carouselContext.state.currentSlide + 1,
                });
              }
            } else {
              if (carouselContext.state.currentSlide > 0) {
                controlRef.current.context.setStoreState({
                  currentSlide: carouselContext.state.currentSlide - 1,
                });
              }
            }
          }
          maxDeltaX = 0;
          setTimeout(() => {
            checkForScroll = true;
          }, 1000);
        }
      }
    };

    // modern Chrome requires { passive: false } when adding event
    let supportsPassive = false;
    try {
      carouselRef.current.addEventListener(
        'test',
        null,
        Object.defineProperty({}, 'passive', {
          get: function () {
            supportsPassive = true;
          },
        })
      );
    } catch (e) {}

    const wheelOpt = supportsPassive ? { passive: false } : false;
    const wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';
    carouselRef.current.addEventListener(wheelEvent, handleScroll, wheelOpt);
    return () => {
      carouselRef.current.removeEventListener(wheelEvent, handleScroll);
    };
  }, []);

  useEffect(() => {
    if (controlRef.current) {
      const onChange = () => {
        setCurrentSlide(controlRef.current.context.state.currentSlide);
      };
      controlRef.current.context.subscribe(onChange);
      return () => controlRef.current.context.unsubscribe(onChange);
    }
  }, [controlRef.current]);
  
  const getExternalLink = () => {
    var link = cardData && cardData.Link && cardData.Link.HasLink ? cardData.Link : null;

    if(cardData.ImageTextCarousel && controlRef.current){
      for(var i = 0; i<cardData.ImageTextCarousel.length; i++){
        var slide = cardData.ImageTextCarousel[i];
        const carouselContext = controlRef.current.context;
        if(slide && slide.Link && slide.Link.HasLink && 
          carouselContext.state.currentSlide === i){
            link = slide.Link;
        }
      }
    }
    
    if(link && link.Text && link.Text.length > 0 && cardData.Key >= 0){
      var isExternal = link.Type === linkTypes.EXTERNAL;
      return (
        <div className="static-content">
          <ExternalLink index={cardData.Key} text={link.Text} link={isExternal ? link.ExternalURL.Text : `/${link.InternalURL.BasePath && link.InternalURL.BasePath.Text}/${link.InternalURL.Path && link.InternalURL.Path.Path}`} isExternal={isExternal} isInline={isInline} />
        </div>
      );
    } else {
      return null;
    }
  };

  var classname = "infographic-carousel " + (isInline ? "inline" : "");
  var numSwipes = Math.ceil((cardData.ImageTextCarousel || []).length - elmtsPerSlide + 1);

  const renderDots = (props) => {
    if(!props){
      return null;
    }
    const {
      currentSlide,
      totalSlides,
      visibleSlides,
      disableActiveDots,
      showAsSelectedForCurrentSlideOnly
    } = props;

    const dots = [];
    for (let i = 0; i < totalSlides; i += 1) {
      const multipleSelected = i >= currentSlide && i < (currentSlide + visibleSlides);
      const singleSelected = i === currentSlide;
      const selected = showAsSelectedForCurrentSlideOnly ? singleSelected : multipleSelected;
      const slide = i >= totalSlides - visibleSlides ? totalSlides - visibleSlides : i;
      const handleClick = selected ? null : () => {handleDotClick(i)};
      dots.push(
        <div className="dotwrap" key={i} onClick={handleClick}>
          <Dot
            slide={slide}
            selected={selected}
            disabled={true}>
              <span/>
          </Dot>
        </div>
      );
    }
    return dots;
  };

  const handleDotClick = (index) => {
    controlRef.current.context.setStoreState({
      currentSlide: index,
    });
  };

  console.log(currentSlide)
  console.log(cardData.ImageTextCarousel.length / elmtsPerSlide);

  return (
    <div className={classname} ref={carouselRef}>
      {getExternalLink()}
      <CarouselProvider
        totalSlides={(cardData.ImageTextCarousel || []).length}
        naturalSlideWidth={naturalSlideWidth}
        naturalSlideHeight="100vh"
        dragStep={1}
        visibleSlides={elmtsPerSlide}
        lockOnWindowScroll={false}
        dragEnabled={(cardData.ImageTextCarousel || []).length > 1}
        touchEnabled={(cardData.ImageTextCarousel || []).length > 1}
        interval={10000}
      >
        <div className="btn-next-flexbox">
          <div className="btn-next-flexcontainer">
            <div className="btn-next-container">
              {
                currentSlide < ((cardData.ImageTextCarousel || []).length / elmtsPerSlide) - 1
                &&
                <ButtonNext />
              }
            </div>
          </div>
        </div>

        <div className="btn-back-flexbox">
          <div className="btn-back-flexcontainer">
            <div className="btn-back-container">
              {
                currentSlide > 0
                &&
                <ButtonBack />
              }
            </div>
          </div>
        </div>

        <Slider classNameAnimation="carousel-holder" moveThreshold="0.02">
          {(cardData.ImageTextCarousel || []).map((slide, i) => (
            <Slide key={slide.Key} index={i}>
              <div className="content" >
                <div className="width-wrapper">
                  <div className="content-wrapper">
                    <div className={`infographic-text ${elmtsPerSlide === 1 ? 'venetian' : ''}`} role={ADAARIARoles.Group}>
                      <div className="target-text">
                        {slide.Header && 
                          <div className="header" role={ADAARIARoles.Heading}>
                            {elmtsPerSlide === 1 && <VenetianBlindsText show={isActive && controlRef && controlRef.current && controlRef.current.context.getStoreState() && controlRef.current.context.getStoreState().currentSlide === i} delay={animationDelay} />}
                            {slide.Header}</div>
                        }
                        <div className="ada-hidden">.</div>
                        <div className="body">
                          {elmtsPerSlide === 1 && <VenetianBlindsText show={isActive && controlRef && controlRef.current && controlRef.current.context.getStoreState() && controlRef.current.context.getStoreState().currentSlide === i} delay={animationDelay} />}
                          {formatParagraphs(slide.Body)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Slide>
          ))}
        </Slider>

        <DotGroup
          ref={controlRef}
          showAsSelectedForCurrentSlideOnly={true}
          disableActiveDots={true}
          style={{
            visibility: (cardData.ImageTextCarousel || []).length > elmtsPerSlide ? '' : 'hidden',
          }}
          totalSlides={numSwipes}
          renderDots={renderDots}
        />
      </CarouselProvider>
    </div>
  );
};

export default InfographicCarousel;
